import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Grid,
} from "@mui/material";
import io from "socket.io-client";
import useGameSounds from "../../../hooks/useGameSounds";
import useHolPrepBg from "../../../hooks/useHolPrepBg";
import useHolWheelSpinSound from "../../../hooks/useHolWheelSpinSound";
import HotSeatAnnounceModal from "../../games/HotSeatAnnounceModal";
import GameOverResultsModal from "../../games/GameOverResultsModal";
import FlippableCard from "../../games/FlippableCard";
import HolUserWheelStage from "../../games/HolUserWheelStage";
import HolGameOverScreen from "../../games/HolGameOverScreen";
import HolSessionCountdown, { useHolSessionCountdown } from "../../games/HolSessionCountdown";
import HolIntroStep from "../../AdminSide/games/hol/HolIntroStep";
import HolHowToPlayStep from "../../AdminSide/games/hol/HolHowToPlayStep";
import { holPageSx } from "../pages/holTheme";
import { playClueNote } from "../../../utils/audioClue";
import {
  HOL_HOT_SEAT_CLAP_URL,
  HOL_CARD_FLIP_URL,
  playHolSound,
  stopHolAudio,
} from "../../../constants/holDefaultSounds";
import { bindGameResync } from "../../../utils/bindGameResync";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const FINAL_RESULTS_HOLD_MS = 90000;
const RESULT_BEFORE_GAME_OVER_MS = 3000;

const LAST_CALL_RESULT = {
  correct: { label: "Correct", color: "#a8e063" },
  wrong: { label: "Incorrect", color: "#ff6b3d" },
  tie: { label: "Tie", color: "#f5d76e" },
  push: { label: "Push", color: "#f5d76e" },
};

const SpectatorHigherOrLower = () => {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [, setError] = useState(null);
  const [lobbyPhase, setLobbyPhase] = useState("intro");
  const [numberOfCards, setNumberOfCards] = useState(5);
  const { countdownEndsAt, applyCountdown, clearCountdown } = useHolSessionCountdown();
  const [prepOn, setPrepOn] = useState(false);
  const [hotSeatAnnounce, setHotSeatAnnounce] = useState(null);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const socketRef = useRef(null);
  const stateRef = useRef(null);
  const { sounds, play, playLoop, stopBgMusic } = useGameSounds("higher_or_lower");
  const soundsRef = useRef(sounds);
  const playRef = useRef(play);
  const prevRevealedRef = useRef(null);
  const clapAudioRef = useRef(null);
  const completedKeyRef = useRef(null);
  const resultTimerRef = useRef(null);
  stateRef.current = state;

  useEffect(() => {
    soundsRef.current = sounds;
  }, [sounds]);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  const stopWheelSpinSound = useHolWheelSpinSound({
    sounds,
    wheel: state?.user_wheel,
  });

  const playHotSeatClap = useCallback(() => {
    stopWheelSpinSound();
    playHolSound(soundsRef.current, playRef.current, "hot_seat", HOL_HOT_SEAT_CLAP_URL);
  }, [stopWheelSpinSound]);

  const playCardFlip = useCallback(() => {
    playHolSound(soundsRef.current, playRef.current, "card_flip", HOL_CARD_FLIP_URL);
  }, []);

  const closeHotSeatAnnounce = useCallback(() => {
    setHotSeatAnnounce(null);
  }, []);

  const closeResultsModal = useCallback(() => {
    setResultsModalOpen(false);
    stopHolAudio(clapAudioRef.current);
    clapAudioRef.current = null;
  }, []);

  useEffect(() => {
    const sid = state?.session_id ?? null;
    const count = state?.revealed_count ?? 0;
    if (!sid) {
      prevRevealedRef.current = null;
      return;
    }
    if (prevRevealedRef.current === null) {
      prevRevealedRef.current = { sid, count };
      return;
    }
    if (prevRevealedRef.current.sid !== sid) {
      prevRevealedRef.current = { sid, count };
      return;
    }
    if (count > prevRevealedRef.current.count) {
      playCardFlip();
    }
    prevRevealedRef.current = { sid, count };
  }, [state?.session_id, state?.revealed_count, playCardFlip]);

  useEffect(() => {
    const runOver = !!state?.game_complete && !state?.user_wheel?.phase;
    if (!runOver) {
      if (resultTimerRef.current) {
        clearTimeout(resultTimerRef.current);
        resultTimerRef.current = null;
      }
      completedKeyRef.current = null;
      setResultsModalOpen(false);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
      return;
    }
    const key = `${state.session_id}-${state.hot_seat_display_name}-${state.score}-${state.revealed_count}`;
    if (completedKeyRef.current === key) return;
    if (resultTimerRef.current) {
      clearTimeout(resultTimerRef.current);
      resultTimerRef.current = null;
    }
    completedKeyRef.current = key;
    setResultsModalOpen(false);
    resultTimerRef.current = setTimeout(() => {
      resultTimerRef.current = null;
      setResultsModalOpen(true);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = playHolSound(
        soundsRef.current,
        null,
        "hot_seat",
        HOL_HOT_SEAT_CLAP_URL,
        { loop: false, forceDirect: true },
      );
    }, RESULT_BEFORE_GAME_OVER_MS);
  }, [
    state?.game_complete,
    state?.user_wheel?.phase,
    state?.session_id,
    state?.hot_seat_display_name,
    state?.score,
    state?.revealed_count,
  ]);

  useEffect(() => {
    return () => {
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
    };
  }, []);

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/higher-or-lower/single-player/active`);
      const data = await res.json();
      if (data.success) setPrepOn(!!data.prep_music);
      if (data.success && data.active && data.state) {
        setState(data.state);
        if (data.countdown) applyCountdown(data.countdown);
        setError(null);
      } else {
        setState(null);
        if (data.lobby_phase === "rules" || data.lobby_phase === "intro") {
          setLobbyPhase(data.lobby_phase);
        }
        setError(null);
      }
    } catch (err) {
      setError("Unable to connect");
    } finally {
      setLoading(false);
    }
  }, [applyCountdown]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/settings/public/higher-or-lower-cards`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setNumberOfCards(parseInt(data.numberOfCards, 10) || 5);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;

    const joinRoom = () => {
      const sid = stateRef.current?.session_id;
      if (sid) socket.emit("holsp:join-session", { sessionId: sid });
    };

    socket.on("connect", () => {
      joinRoom();
      fetchState();
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected) joinRoom();
      fetchState();
    });

    socket.on("holsp:clue_played", (payload) => {
      if (payload?.note) playClueNote(payload.note, soundsRef.current);
    });

    socket.on("holsp:state_updated", (payload) => {
      setState(payload);
      setError(null);
    });

    socket.on("holsp:session_countdown", (payload) => applyCountdown(payload));

    socket.on("holsp:lobby_phase", (payload) => {
      if (payload?.phase === "rules" || payload?.phase === "intro") {
        setLobbyPhase(payload.phase);
      }
    });

    socket.on("holsp:prep_music", (payload) => {
      setPrepOn(!!payload?.active);
    });

    socket.on("holsp:return_to_start", () => {
      setFinalResults(null);
      setLobbyPhase("intro");
      setResultsModalOpen(false);
      setHotSeatAnnounce(null);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
    });

    socket.on("holsp:session_ended", (payload) => {
      setPrepOn(false);
      if (payload?.final_results) setFinalResults(payload.final_results);
      setState(null);
      setError("Session ended");
      setHotSeatAnnounce(null);
      setResultsModalOpen(false);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
      clearCountdown();
    });

    socket.on("holsp:session_restarted", ({ session_id: newSessionId }) => {
      const oldSid = stateRef.current?.session_id;
      if (oldSid && oldSid !== newSessionId) {
        socket.emit("holsp:leave-session", { sessionId: oldSid });
      }
      if (newSessionId) {
        socket.emit("holsp:join-session", { sessionId: newSessionId });
      }
      setHotSeatAnnounce(null);
      setResultsModalOpen(false);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
      fetchState();
    });

    socket.on("holsp:hot_seat_selected", (payload) => {
      setHotSeatAnnounce({
        display_name: payload?.display_name || "Player",
        profile_picture: payload?.profile_picture || null,
      });
      playHotSeatClap();
      fetchState();
    });

    const poll = setInterval(fetchState, 12000);

    return () => {
      unbindResync();
      clearInterval(poll);
      const sid = stateRef.current?.session_id;
      if (sid) socket.emit("holsp:leave-session", { sessionId: sid });
      socket.disconnect();
    };
  }, [fetchState, playHotSeatClap, applyCountdown, clearCountdown]);

  useEffect(() => {
    if (state?.session_id && socketRef.current?.connected) {
      socketRef.current.emit("holsp:join-session", { sessionId: state.session_id });
    }
  }, [state?.session_id]);

  useEffect(() => {
    if (state?.session_id && finalResults && state.session_id !== finalResults.session_id) {
      setFinalResults(null);
    }
  }, [state?.session_id, finalResults]);

  useEffect(() => {
    if (!finalResults) return undefined;
    const t = setTimeout(() => setFinalResults(null), FINAL_RESULTS_HOLD_MS);
    return () => clearTimeout(t);
  }, [finalResults]);

  const showGameOver =
    !!finalResults && (!state?.session_id || state.session_id === finalResults.session_id);

  useHolPrepBg({
    sounds,
    playLoop,
    stopBgMusic,
    prepOn,
    countdownEndsAt,
    wheelPhase: state?.user_wheel?.phase,
    suppressed: showGameOver,
  });

  const countdownOverlay = countdownEndsAt ? (
    <HolSessionCountdown endsAt={countdownEndsAt} onDone={clearCountdown} />
  ) : null;

  if (loading) {
    return (
      <>
        {countdownOverlay}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#0a0a0a" }}>
          <CircularProgress sx={{ color: "#E040FB" }} />
        </Box>
      </>
    );
  }

  if (finalResults && (!state?.session_id || state.session_id === finalResults.session_id)) {
    return (
      <>
      {countdownOverlay}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          background:
            "radial-gradient(ellipse 70% 50% at 20% 0%, rgba(168,224,99,0.14) 0%, transparent 60%)," +
            "radial-gradient(ellipse 60% 50% at 85% 10%, rgba(255,107,61,0.12) 0%, transparent 55%), #0a0a0a",
        }}
      >
        <HolGameOverScreen results={finalResults} large />
      </Box>
      </>
    );
  }

  if (!state?.session_id) {
    return (
      <>
      {countdownOverlay}
      <Box sx={{ ...holPageSx, color: "#fff" }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 1, sm: 2 }, py: 2 }}>
          {lobbyPhase === "rules" ? (
            <HolHowToPlayStep numberOfCards={numberOfCards} waiting />
          ) : (
            <HolIntroStep numberOfCards={numberOfCards} waiting />
          )}
        </Box>
      </Box>
      </>
    );
  }

  const promptCard =
    state.revealed_cards && state.revealed_cards.length
      ? state.revealed_cards[state.revealed_cards.length - 1]
      : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#0a0a0a",
        py: 3,
        px: 2,
        textAlign: "center",
      }}
    >
      {countdownOverlay}
      <HotSeatAnnounceModal
        open={!!hotSeatAnnounce}
        onClose={closeHotSeatAnnounce}
        displayName={hotSeatAnnounce?.display_name}
        profilePicture={hotSeatAnnounce?.profile_picture}
      />

      <GameOverResultsModal
        open={resultsModalOpen}
        onClose={closeResultsModal}
        hideClose
        score={state.score ?? 0}
        outOf={Math.max(0, (state.total_cards || 1) - 1)}
        playerLabel={state.hot_seat_display_name || null}
        scoreCaption="Score"
        subtitle={null}
      />

      <Typography variant="h4" sx={{ color: "#E040FB", fontWeight: 800, mb: 2 }}>
        Higher or Lower
      </Typography>

      {state.user_wheel?.phase ? (
        <HolUserWheelStage userWheel={state.user_wheel} />
      ) : (
      <>
      <Chip
        sx={{ mb: 2 }}
        color={state.hot_seat_display_name ? "warning" : "default"}
        label={
          state.hot_seat_display_name
            ? `Now playing: ${state.hot_seat_display_name}`
            : "Waiting for host to select hot seat"
        }
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Score: {state.score ?? 0}
        {state.last_result ? (
          <Chip
            size="small"
            sx={{ ml: 1 }}
            label={LAST_CALL_RESULT[state.last_result]?.label || state.last_result}
            color={
              state.last_result === "correct"
                ? "success"
                : state.last_result === "tie" || state.last_result === "push"
                  ? "info"
                  : "error"
            }
          />
        ) : null}
      </Typography>

      {state.game_complete && !resultsModalOpen && LAST_CALL_RESULT[state.last_result] ? (
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: "3.2rem", sm: "5.5rem" },
            lineHeight: 1,
            letterSpacing: "-0.03em",
            color: LAST_CALL_RESULT[state.last_result].color,
            textShadow: `0 0 28px ${LAST_CALL_RESULT[state.last_result].color}88`,
            mb: 2,
          }}
        >
          {LAST_CALL_RESULT[state.last_result].label}
        </Typography>
      ) : null}

      <Grid
        container
        spacing={3}
        justifyContent="center"
        sx={{ mb: 2, minHeight: { xs: 220, sm: 270, md: 320 } }}
      >
        {Array.from({ length: state.total_cards || 0 }).map((_, index) => {
          const card = state.revealed_cards?.[index];
          const revealed = !!card;
          return (
            <Grid
              item
              xs={4}
              sm={6}
              md={2.4}
              key={`${state.session_id}-${state.hot_seat_display_name || "none"}-${index}`}
            >
              <FlippableCard
                card={card}
                revealed={revealed}
                maxHeight={{ xs: 160, sm: 250, md: 300 }}
                minHeight={{ xs: 160, sm: 250, md: 300 }}
              />
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: "0.9rem", sm: "1rem" }, color: "text.secondary" }}>
        {promptCard
          ? `Is next card higher or lower than ${promptCard.displayName || promptCard.name}?`
          : "Waiting for first card reveal..."}
      </Typography>

      {state.pending_user_guess && (
        <Alert severity="info" sx={{ maxWidth: 620, mx: "auto", mb: 2 }}>
          Locked answer: <strong>{state.pending_user_guess.toUpperCase()}</strong> — waiting for reveal...
        </Alert>
      )}
      </>
      )}
    </Box>
  );
};

export default SpectatorHigherOrLower;
